/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Type } from '@gh/core-type';
import { GridApi, ICellEditorParams, IRowNode, RowPinnedType } from 'ag-grid-community';
import { defer, isFunction, isNil } from 'lodash';
import { EditableCellModel } from '../../../state/grid-state.interfaces';
import { createCellFormControl } from '../../grid-cell.utils';
import { GridCellEditorComponent } from '../../grid-component.interfaces';

const STOPPED_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);

export class AbstractGridInputCellEditorComponent implements GridCellEditorComponent, AfterViewInit, OnDestroy {
  required: boolean = false;

  control: FormControl;
  editorOptions?: any;

  @ViewChild('input', { static: false }) inputEl: ElementRef;

  type?: Type<any>;

  private isSelectOnFocus = true;
  private stopEditing: (suppressNavigateAfterEdit?: boolean) => void;
  private gridApi: GridApi;
  private editNode: IRowNode;
  private editRowIndex: number;
  private editColKey: string;
  private editRowPinned: RowPinnedType;
  private focusTimeout: number;

  get maxLength(): number {
    const { type } = this;
    return type && type['maxLength'] || -1;
  }

  ngAfterViewInit(): void {
    this.focusTimeout = defer(() => {
      this.inputEl.nativeElement.focus();
      if (this.isSelectOnFocus) {
        this.inputEl.nativeElement.select();
      }
      this.focusTimeout = 0;
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.focusTimeout);
  }

  agInit(params: ICellEditorParams): void {
    const model: EditableCellModel = params['modelFactory']({
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    });

    const typeOrFactory = params['type'];

    this.required = model.required;
    this.control = createCellFormControl(model);
    this.type = isFunction(typeOrFactory) ? typeOrFactory(params.node.data) : typeOrFactory;
    this.stopEditing = params.stopEditing;
    this.gridApi = params.api;
    this.editNode = params.node;
    this.editRowIndex = params.rowIndex;
    this.editColKey = params.column.getColId();
    this.editRowPinned = params.node.rowPinned;
    this.editorOptions = params['options'];

    const charPress = params.eventKey && params.eventKey.length === 1 ? params.eventKey : undefined;
    if (charPress) {
      this.isSelectOnFocus = false;
      this.setFirstChar(charPress);
    }
  }

  getValue(): any {
    return this.control.value;
  }

  onBlur(): void {
    setTimeout(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest('.ag-popup-editor')) {
        return;
      }
      const el = this.inputEl && this.inputEl.nativeElement;
      if (!el) {
        this.stopEditing();
        return;
      }
      if (active === el) {
        return;
      }
      const popup = el.closest('.ag-popup-editor');
      if (popup && popup.contains(active)) {
        return;
      }
      this.stopEditing();
    }, 0);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const { key } = event;
    if (key === 'ArrowUp') {
      if (event.target instanceof HTMLTextAreaElement) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.moveToAdjacentRowInColumn(-1);
      return;
    }
    if (key === 'ArrowDown') {
      if (event.target instanceof HTMLTextAreaElement) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.moveToAdjacentRowInColumn(1);
      return;
    }
    if (STOPPED_KEYS.has(key)) {
      event.stopPropagation();
    }
  }

  private afterStopEditing(action: () => void): void {
    defer(action);
  }

  private moveToAdjacentRowInColumn(delta: -1 | 1): void {
    const api = this.gridApi;
    if (!api) {
      return;
    }
    const n = this.editNode;
    const currentIndex = n != null && n.rowIndex != null
      ? n.rowIndex
      : this.editRowIndex;
    const nextRowIndex = currentIndex + delta;
    if (nextRowIndex < 0 || nextRowIndex >= api.getDisplayedRowCount()) {
      return;
    }
    const colKey = this.editColKey;
    const rowPinned = this.editRowPinned;
    this.stopEditing(true);
    this.afterStopEditing(() => {
      if (nextRowIndex < 0 || nextRowIndex >= api.getDisplayedRowCount()) {
        return;
      }
      if (!api.getDisplayedRowAtIndex(nextRowIndex)) {
        return;
      }
      api.startEditingCell({ rowIndex: nextRowIndex, colKey, rowPinned: rowPinned ?? void 0 });
    });
  }

  protected setFirstChar(char: string): void {
    if (this.type) {
      this.control.setValue(isNil(this.type.validateFormat(char)) ? this.type.parse(char) : void 0);
    } else {
      this.control.setValue(char);
    }
  }
}
